import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import copilotStore from "Stores/copilotStore";
import copilotDrawerStore, {
  DRAWER_MIN_WIDTH,
  DRAWER_MAX_WIDTH
} from "Stores/copilotDrawerStore";
import SparkleIcon from "Components/common/SparkleIcon";
import copilotEditorStore from "Stores/copilotEditorStore";
import CopilotCompose from "./CopilotCompose";
import ChatThread from "./ChatThread";
import Composer from "./Composer";

/*
 * The copilot as a panel over the current page instead of a separate route --
 * the page keeps its state, its scroll and its selection while you ask about it.
 *
 * Floating overlays and dims the page; pinned shrinks .mainContent instead, so
 * both stay usable together. It is the same conversation as /copilot: ChatThread
 * and Composer read copilotStore directly, so nothing here is a second engine.
 */
const CopilotDrawer = observer(props => {
  const { findings = [] } = props || {};
  const { open, pinned, canPin, surface, entityId } = copilotDrawerStore;
  const width = copilotDrawerStore.effectiveWidth;
  const history = useHistory();
  const [dragging, setDragging] = useState(false);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  // Findings describe the PAGE, not the conversation, so they stay available
  // once a chat starts -- collapsed by default then, so the thread has room.
  const [findingsOpen, setFindingsOpen] = useState(null);
  const asideRef = useRef(null);
  const hasThread = copilotStore.messages.length > 0;
  // Opened from an editor's wand: the drawer is a writing surface for that
  // editor, not a page assistant. Page findings would be noise here.
  const composing = surface === "email_editor";

  // The model's read is fetched here rather than by the page, because the pages
  // are not observers -- they would never re-render when the drawer opens. Only
  // fires on open, so browsing never costs a model call.
  useEffect(() => {
    if (!open || !surface || composing) return undefined;
    let live = true;
    setInsights(null);
    setInsightsLoading(true);
    copilotStore.fetchInsights(surface, entityId).then(data => {
      if (!live) return;
      setInsights(data);
      setInsightsLoading(false);
    });
    return () => {
      live = false;
    };
  }, [open, surface, entityId]);

  // A conversation is created lazily -- opening the drawer is the first moment
  // we know the user wants one. Anything the page asked on our behalf waits in
  // pendingAutoSend until the transport is actually up; sending it straight
  // after openFor() would fire before there is a conversation to send it to.
  // Coming back from the full page: reopen where we left off and reload the
  // thread by id, because the copilot page reset the store on its way out.
  useEffect(() => {
    const pending = copilotDrawerStore.consumeReopen();
    if (!pending) return;
    copilotDrawerStore.openFor(pending.surface, pending.entityId);
    if (pending.conversationId) {
      copilotStore.loadConversation(pending.conversationId);
    }
  }, []);

  // Opening the drawer no longer opens a connection: the findings are computed
  // locally and most opens never become a conversation. sendUserMessage
  // connects on the first message instead.
  useEffect(() => {
    if (!open) return;
    const pending = copilotStore.consumePendingAutoSend();
    if (pending) copilotStore.sendUserMessage(pending);
  }, [open, copilotStore.pendingAutoSend]);

  useEffect(() => {
    const onKey = e => {
      if (e.key === "Escape" && copilotDrawerStore.open) copilotDrawerStore.close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Pinning shrinks the page rather than covering it. Driving it from a class +
  // custom property keeps the layout rule in SCSS and out of here.
  useEffect(() => {
    const root = document.body;
    const on = open && pinned;
    root.classList.toggle("sgCopilotPinned", on);
    document.documentElement.style.setProperty(
      "--sgCopilotDrawerWidth",
      on ? `${width}px` : "0px"
    );
    return () => {
      root.classList.remove("sgCopilotPinned");
      document.documentElement.style.setProperty("--sgCopilotDrawerWidth", "0px");
    };
  }, [open, pinned, width]);

  useEffect(() => {
    const onResize = () => copilotDrawerStore.setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Leaving the page closes it. Without this the open flag outlives the page
  // and the drawer reappears by itself on the next sequence you visit, which
  // reads as auto-opening -- the one thing this is not supposed to do.
  useEffect(() => () => copilotDrawerStore.close(), []);

  const startDrag = useCallback(e => {
    e.preventDefault();
    setDragging(true);
    const onMove = ev => {
      copilotDrawerStore.setWidth(window.innerWidth - ev.clientX);
    };
    const onUp = () => {
      setDragging(false);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, []);

  if (!open) return null;

  // Carry the thread across rather than starting a fresh one. conversationId is
  // the PERSISTED chat id, and persistConversation() creates it lazily on the
  // first message -- so a drawer conversation that has messages but was never
  // persisted used to land on /copilot/new and be thrown away.
  const openFullPage = async () => {
    const path = window.location.pathname + window.location.hash;
    copilotDrawerStore.setReturnTo(path);
    copilotDrawerStore.close();
    let id = copilotStore.conversationId;
    if (!id && copilotStore.messages.length) {
      await copilotStore.persistConversation();
      id = copilotStore.conversationId;
    }
    history.push(id ? `/copilot/${id}` : "/copilot/new");
  };

  const modelFindings = (insights && insights.findings) || [];
  const nothingToShow =
    !findings.length && !modelFindings.length && !insightsLoading && !insights;

  // Portalled out of .mainContent on purpose. Pinning makes .mainContent the
  // containing block for its fixed descendants so the whole shell reflows in
  // one rule -- and the drawer must sit OUTSIDE that box, or it would be
  // positioned against the very width it is subtracting.
  return ReactDOM.createPortal(
    <>
      <aside
        ref={asideRef}
        className={`copilotDrawer ${pinned ? "pinned" : "floating"} ${
          dragging ? "dragging" : ""
        }`}
        style={{ width: `${width}px` }}
        aria-label="Copilot"
      >
        <div
          className="copilotDrawerResize"
          onMouseDown={startDrag}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize copilot"
        />
        <div className="copilotDrawerHead">
          <span className="cdTitle">
            <SparkleIcon className="cdSpark" /> Copilot
          </span>
          <span className="cdSpacer" />
          {canPin && (
            <button
              type="button"
              className="cdIconBtn"
              aria-pressed={pinned}
              onClick={() => copilotDrawerStore.setPinned(!pinned)}
              title={pinned ? "Let it float over the page" : "Dock it beside the page"}
            >
              {pinned ? "Unpin" : "Pin"}
            </button>
          )}
          <button type="button" className="cdIconBtn" onClick={openFullPage}>
            Full page
          </button>
          <button
            type="button"
            className="cdIconBtn cdClose"
            onClick={() => copilotDrawerStore.close()}
            aria-label="Close copilot"
          >
            <i className="material-icons">close</i>
          </button>
        </div>

        {composing ? (
          <CopilotCompose />
        ) : (
        <div className="copilotDrawerBody">
          {(() => {
            const total = findings.length + modelFindings.length;
            const expanded = findingsOpen === null ? !hasThread : findingsOpen;
            if (!total && !insightsLoading && !nothingToShow) return null;
            return (
              <div
                className={`cdFindingsWrap ${expanded ? "open" : ""} ${
                  hasThread ? "withThread" : ""
                }`}
              >
                {hasThread && (
                  <button
                    type="button"
                    className="cdFindingsToggle"
                    onClick={() => setFindingsOpen(!expanded)}
                  >
                    <SparkleIcon className="cdSpark" />
                    <span className="cdFindingsToggleLabel">
                      This page
                      {total ? ` · ${total} insight${total === 1 ? "" : "s"}` : ""}
                      {insightsLoading && (
                        <span className="cdFindingsPending"> · still reading…</span>
                      )}
                    </span>
                    <i className="material-icons">
                      {expanded ? "expand_less" : "expand_more"}
                    </i>
                  </button>
                )}
                {expanded && (
            <div className="cdFindings">
              {findings.map((finding, index) => (
                <div className={`cdFinding ${finding.tone || "neutral"}`} key={index}>
                  <div className="cdFindingLabel">{finding.label}</div>
                  <div className="cdFindingText">{finding.text}</div>
                  {(finding.actions || []).length > 0 && (
                    <div className="cdFindingActions">
                      {finding.actions.map((action, i) => (
                        <button
                          type="button"
                          key={i}
                          className={`cdAct ${i === 0 ? "pri" : ""}`}
                          onClick={action.run}
                        >
                          {action.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {insightsLoading && <div className="cdFinding linear-background cdSkeleton" />}
              {modelFindings.length > 0 && (
                <div className="cdFindingsDivider">Deeper read</div>
              )}
              {modelFindings.map((finding, index) => (
                <div className="cdFinding neutral" key={`m${index}`}>
                  <div className="cdFindingText">{finding.text}</div>
                </div>
              ))}
              {nothingToShow && (
                <div className="cdFinding neutral">
                  <div className="cdFindingText">
                    {surface === "sequences"
                      ? "Nothing worth flagging across your sequences right now. Ask me anything about them below."
                      : "Nothing worth flagging on this sequence right now. Ask me anything about it below."}
                  </div>
                </div>
              )}
            </div>
                )}
              </div>
            );
          })()}
          <div className="cdThreadScroll">
            <ChatThread />
          </div>
        </div>
        )}

        {!composing && (
          <div className="copilotDrawerFoot">
            <Composer />
          </div>
        )}
      </aside>
    </>,
    document.body
  );
});

export default CopilotDrawer;
export { DRAWER_MIN_WIDTH, DRAWER_MAX_WIDTH };
